const set = require('lodash/set')
const get = require('lodash/get')
const getSnapshotId = require('./tools/get-snapshot-id')
const toDocIdProperties = require('./tools/to-doc-id-properties')
const toLocationProperties = require('./tools/to-location-properties')

/**
 * @typedef {Object} MainApi
 * @property {import('./index').ShipmentApi} shipment
 * @property {import('../product')} product
 */

/**
 * @typedef {'CouchProduct'} PriceSource
*/

class ShipmentPriceListApi {
  /**
   * @param {MainApi} mainApi
   */
  constructor (mainApi) {
    this.mainApi = mainApi
  }

  /**
   * @param {string} shipmentId
   * @param {Object.<string, {
   *   quantity: number
   *   paymentType: string
   *   checked: boolean
   *   skuMarket: string
   *   destinationSku: string
   *   originSku: string
   * }>} countsWithTranslations
   * @param {PriceSource} priceSource
   */
  async upsert (shipmentId, countsWithTranslations, priceSource) {
    if (!countsWithTranslations) {
      throw new Error('Counts with sku translations required')
    }

    const shipment = await this.mainApi.shipment.findById(shipmentId)
    const referenceDate = new Date(shipment.date)
    const timeStamp = new Date().toJSON()
    const unitPrices = await this.getUnitPrices(shipment, countsWithTranslations, priceSource, referenceDate)

    // update price list snapshot
    if (shipment.priceListId) {
      const priceSnapshot = await this.mainApi.state.shipmentsDb.get(shipment.priceListId)
      const updatedUnitPrices = { ...get(priceSnapshot, 'unitPrices', {}), ...unitPrices }
      set(priceSnapshot, 'unitPrices', updatedUnitPrices)
      set(priceSnapshot, 'updatedAt', timeStamp)
      set(priceSnapshot, 'priceReferenceDate', referenceDate.toJSON())
      return this.mainApi.state.shipmentsDb.bulkDocs([priceSnapshot])
    }

    const idProps = toDocIdProperties(shipment.id)
    const id = getSnapshotId(
      { device: { id: 0 } },
      {
        status: idProps.status,
        shipmentNo: idProps.shipmentNo,
        date: idProps.date,
        origin: toLocationProperties(idProps.origin),
        destination: toLocationProperties(idProps.destination)
      }
    ) + `:price:list`

    const snapshot = {
      _id: id,
      type: 'price',
      version: '1.0.0',
      createdAt: timeStamp,
      updatedAt: timeStamp,
      unitPrices,
      createdBy: 'sku-price-handler-lambda',
      updatedBy: 'sku-price-handler-lambda',
      priceReferenceDate: referenceDate.toJSON(),
      source: priceSource
    }
    return this.mainApi.state.shipmentsDb.put(snapshot)
  }

  /**
    * @param {any} shipment
    * @param {Object.<string, {
    *   quantity: number
    *   paymentType: string
    *   checked: boolean
    *   skuMarket: string
    *   destinationSku: string
    *   originSku: string
    * }>} countsWithTranslations
    * @param {PriceSource} priceSource
    * @param {Date} referenceDate
  */
  async getUnitPrices (shipment, countsWithTranslations, priceSource, referenceDate) {
    const destinationSkus = Object.values(countsWithTranslations).map(s => `product:${s.destinationSku}`)
    let productPrices

    if (priceSource === 'CouchProduct') {
      productPrices = await this.getCouchProductPriceList(destinationSkus, referenceDate)
    } else {
      throw new Error('Unrecognized price source')
    }

    return Object.keys(shipment.counts).reduce((acc, sku) => {
      const referenceProduct = `product:${countsWithTranslations[sku] && countsWithTranslations[sku].destinationSku}`
      acc[sku] = {
        price: productPrices[referenceProduct]
      }
      return acc
    }, {})
  }

  /**
   * @param {string[]} skus
   * @param {Date} referenceDate
   */
  async getCouchProductPriceList (skus, referenceDate) {
    const formattedDate = new Date(referenceDate)
    const products = await this.mainApi.product.getByIds(skus)
    return products.reduce((acc, product) => {
      const productPrices = get(product, 'prices', [])
      const price = Number.parseFloat(this.mainApi.product.tools.getPrice(productPrices, formattedDate.toJSON()))
      acc[product._id] = Number.isNaN(price) ? null : price
      return acc
    }, {})
  }
}

module.exports = ShipmentPriceListApi
